import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { fmtDate } from "@/lib/constants";
import { CHANGE_TYPE_STYLES, PRIORITY_STYLES, READINESS_STYLES } from "@/lib/reconConstants";
import { GitCompareArrows, Loader2, ChevronDown, ArrowRight, TrendingUp, TrendingDown, AlertTriangle, Lock } from "lucide-react";

const Badge = ({ className, children }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${className}`}>{children}</span>
);
const skey = (s) => `${s.reconciliation_id}::${s.version}`;
const SUMMARY_META = [
  { key: "newly_ready", label: "Newly ready", tone: "emerald", icon: TrendingUp },
  { key: "regressed", label: "Regressed", tone: "red", icon: TrendingDown },
  { key: "newly_identified", label: "Newly identified", tone: "amber", icon: AlertTriangle },
  { key: "closed", label: "Closed", tone: "slate" },
  { key: "evidence_added", label: "Evidence added", tone: "blue" },
  { key: "evidence_removed", label: "Evidence removed", tone: "amber" },
  { key: "ownership_changes", label: "Ownership changed", tone: "violet" },
];
const TONE = {
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
  red: "bg-red-50 border-red-300 text-red-800",
  amber: "bg-amber-50 border-amber-200 text-amber-800",
  slate: "bg-slate-50 border-slate-200 text-slate-700",
  blue: "bg-blue-50 border-blue-200 text-blue-800",
  violet: "bg-violet-50 border-violet-200 text-violet-800",
};

export default function ReconCompare() {
  const [recons, setRecons] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [snapshots, setSnapshots] = useState([]);
  const [aKey, setAKey] = useState("");
  const [bKey, setBKey] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [onlyChanged, setOnlyChanged] = useState(true);

  useEffect(() => { api.get("/recon/reconciliations").then((r) => setRecons(r.data)).catch(() => {}); }, []);

  const customers = useMemo(() => {
    const map = {};
    recons.forEach((r) => { if ((r.versions || []).length > 0) map[r.customer_id] = r.customer_name; });
    return Object.entries(map).map(([id, name]) => ({ id, name }));
  }, [recons]);

  useEffect(() => {
    setSnapshots([]); setResult(null); setAKey(""); setBKey("");
    if (!customerId) return;
    api.get("/recon/compare/snapshots", { params: { customer_id: customerId } }).then((r) => {
      setSnapshots(r.data);
      if (r.data.length >= 2) { setAKey(skey(r.data[0])); setBKey(skey(r.data[r.data.length - 1])); }
      else if (r.data.length === 1) { setAKey(skey(r.data[0])); }
    }).catch(() => {});
  }, [customerId]);

  const findSnap = (k) => snapshots.find((s) => skey(s) === k);

  const runCompare = () => {
    const a = findSnap(aKey), b = findSnap(bKey);
    if (!a || !b) return toast.error("Select two finalized snapshots.");
    if (aKey === bKey) return toast.error("Snapshot A and B must be different.");
    setLoading(true);
    api.post("/recon/compare", {
      a_reconciliation_id: a.reconciliation_id, a_version: a.version,
      b_reconciliation_id: b.reconciliation_id, b_version: b.version,
    }).then((r) => setResult(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Compare failed.")).finally(() => setLoading(false));
  };

  const rows = useMemo(() => {
    if (!result) return [];
    const filtered = onlyChanged ? result.rows.filter((r) => r.change_type !== "No Material Change") : result.rows;
    const order = { "Regressed": 0, "Newly Identified": 1, "Evidence Removed": 2, "Ownership Changed": 3, "SSP Updated": 4, "Improved": 5, "Evidence Added": 6, "Closed": 7, "No Material Change": 8 };
    return [...filtered].sort((a, b) => (order[a.change_type] ?? 9) - (order[b.change_type] ?? 9));
  }, [result, onlyChanged]);

  return (
    <div>
      <header className="bg-navy px-6 md:px-8 py-6">
        <p className="overline text-gold mb-1.5">Operations · Admin Only</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight flex items-center gap-2">
          <GitCompareArrows className="h-6 w-6 text-gold" aria-hidden="true" /> Compare Versions
        </h1>
        <p className="text-slate-300 text-sm mt-2 max-w-3xl">Compare two finalized reconciliation snapshots for a customer to see readiness deltas, regressions, and control-level changes over time. Snapshots are immutable historical records.</p>
      </header>

      <div className="p-6 md:p-8 space-y-6">
        <div className="rounded-md border border-border bg-card p-4 space-y-4">
          <div className="space-y-1.5 max-w-md"><Label className="text-xs">Customer</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger data-testid="compare-customer"><SelectValue placeholder="Select a customer with finalized versions" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {customerId && snapshots.length < 2 && (
            <p className="text-sm text-amber-700 flex items-center gap-1.5"><Lock className="h-4 w-4" /> This customer needs at least two finalized snapshots to compare. Currently {snapshots.length}.</p>
          )}

          {snapshots.length >= 2 && (
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto] items-end gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Snapshot A (baseline)</Label>
                <Select value={aKey} onValueChange={setAKey}>
                  <SelectTrigger data-testid="compare-snapshot-a"><SelectValue /></SelectTrigger>
                  <SelectContent>{snapshots.map((s) => <SelectItem key={skey(s)} value={skey(s)} disabled={skey(s) === bKey}>v{s.version} · {fmtDate(s.finalized_at)} · {s.readiness || "—"}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="hidden md:flex items-center justify-center pb-2"><ArrowRight className="h-5 w-5 text-gold" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Snapshot B (current)</Label>
                <Select value={bKey} onValueChange={setBKey}>
                  <SelectTrigger data-testid="compare-snapshot-b"><SelectValue /></SelectTrigger>
                  <SelectContent>{snapshots.map((s) => <SelectItem key={skey(s)} value={skey(s)} disabled={skey(s) === aKey}>v{s.version} · {fmtDate(s.finalized_at)} · {s.readiness || "—"}</SelectItem>)}</SelectContent>
                </Select></div>
              <Button className="bg-navy text-white hover:bg-navy/90" onClick={runCompare} disabled={loading || !aKey || !bKey || aKey === bKey} data-testid="compare-run-btn">
                {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <GitCompareArrows className="h-4 w-4 mr-1.5" />} Compare
              </Button>
            </div>
          )}
        </div>

        {result && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-md border border-border bg-card p-4">
                <div className="text-xs text-muted-foreground mb-1">Snapshot A · v{result.a.version}</div>
                <Badge className={READINESS_STYLES[result.a.readiness] || "bg-slate-100 text-slate-600 border-slate-200"}>{result.a.readiness || "—"}</Badge>
                <div className="text-[11px] text-muted-foreground mt-1">{fmtDate(result.a.finalized_at)}</div>
              </div>
              <div className="rounded-md border border-border bg-card p-4 flex flex-col items-center justify-center">
                <ArrowRight className="h-5 w-5 text-gold mb-1" />
                <Badge className={result.readiness_changed ? "bg-gold/20 text-gold-dark border-gold/40" : "bg-slate-100 text-slate-600 border-slate-200"}>
                  {result.readiness_changed ? "Readiness changed" : "Readiness unchanged"}
                </Badge>
              </div>
              <div className="rounded-md border border-border bg-card p-4">
                <div className="text-xs text-muted-foreground mb-1">Snapshot B · v{result.b.version}</div>
                <Badge className={READINESS_STYLES[result.b.readiness] || "bg-slate-100 text-slate-600 border-slate-200"}>{result.b.readiness || "—"}</Badge>
                <div className="text-[11px] text-muted-foreground mt-1">{fmtDate(result.b.finalized_at)}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {SUMMARY_META.map((m) => {
                const val = result.summary[m.key] || 0;
                const Icon = m.icon;
                return (
                  <div key={m.key} className={`rounded-md border p-3 ${val > 0 ? TONE[m.tone] : "bg-card border-border text-muted-foreground"} ${m.key === "regressed" && val > 0 ? "ring-2 ring-red-400" : ""}`} data-testid={`compare-summary-${m.key}`}>
                    <div className="text-2xl font-bold flex items-center gap-1">{Icon && <Icon className="h-4 w-4" />}{val}</div>
                    <div className="text-[11px] font-medium mt-0.5">{m.label}</div>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={onlyChanged} onChange={(e) => setOnlyChanged(e.target.checked)} data-testid="compare-only-changed" /> Show changed controls only
              </label>
              <span className="text-xs text-muted-foreground">{rows.length} controls</span>
            </div>

            <div className="rounded-md border border-border bg-card overflow-hidden">
              <div className="grid grid-cols-[90px_1fr_150px_150px_130px] gap-2 bg-slate-50 border-b border-border px-3 py-2.5 text-xs font-semibold text-navy uppercase tracking-wider">
                <span>Control</span><span>Requirement</span><span>A → B status</span><span>Ownership</span><span>Change</span>
              </div>
              {rows.length === 0 ? (
                <div className="px-4 py-12 text-center text-muted-foreground text-sm" data-testid="compare-empty">No changes to display.</div>
              ) : rows.map((r) => {
                const regressed = r.change_type === "Regressed";
                return (
                  <Collapsible key={r.control_id} className={`border-b border-border last:border-0 ${regressed ? "bg-red-50/60" : ""}`}>
                    <CollapsibleTrigger className="w-full text-left hover:bg-slate-50" data-testid={`compare-row-${r.control_id}`}>
                      <div className="grid grid-cols-[90px_1fr_150px_150px_130px] gap-2 px-3 py-2.5 items-center text-sm">
                        <span className="font-medium text-navy flex items-center gap-1"><ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />{r.control_id}</span>
                        <span className="line-clamp-1 text-xs">{r.requirement}</span>
                        <span className="text-xs"><span className="text-muted-foreground">{r.prev_status || "—"}</span> → <span className="font-medium">{r.curr_status || "—"}</span></span>
                        <span className="text-xs">{r.prev_party !== r.curr_party ? <span className="text-violet-700">{r.prev_party || "—"} → {r.curr_party || "—"}</span> : (r.curr_party || "—")}</span>
                        <span><Badge className={`${CHANGE_TYPE_STYLES[r.change_type]} ${regressed ? "font-bold" : ""}`}>{regressed && <AlertTriangle className="h-3 w-3 mr-1" />}{r.change_type}</Badge></span>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 py-4 bg-slate-50/70 text-xs">
                        <div className="space-y-2">
                          <div className="font-semibold text-navy uppercase tracking-wider text-[10px]">Snapshot A (v{result.a.version})</div>
                          <Field label="Status" value={r.prev_status} />
                          <Field label="Responsible party" value={r.prev_party} />
                          <Field label="Evidence" value={r.prev_evidence} />
                          <Field label="SSP statement" value={r.prev_ssp} />
                          <Field label="Report finding" value={r.prev_finding} />
                          <Field label="Conclusion" value={r.prev_conclusion} />
                        </div>
                        <div className="space-y-2">
                          <div className="font-semibold text-navy uppercase tracking-wider text-[10px]">Snapshot B (v{result.b.version})</div>
                          <Field label="Status" value={r.curr_status} highlight={r.prev_status !== r.curr_status} />
                          <Field label="Responsible party" value={r.curr_party} highlight={r.prev_party !== r.curr_party} />
                          <Field label="Evidence" value={r.curr_evidence} highlight={r.prev_evidence !== r.curr_evidence} />
                          <Field label="SSP statement" value={r.curr_ssp} highlight={r.prev_ssp !== r.curr_ssp} />
                          <Field label="Report finding" value={r.curr_finding} />
                          <Field label="Conclusion" value={r.curr_conclusion} />
                          {r.reviewer && <Field label="Reviewer" value={r.reviewer} />}
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, highlight }) {
  return (
    <div>
      <div className="text-muted-foreground text-[10px] uppercase tracking-wide">{label}</div>
      <div className={`${highlight ? "font-semibold text-gold-dark bg-gold/10 rounded px-1" : "text-slate-700"}`}>{value || "—"}</div>
    </div>
  );
}
